import { Controller } from '@hotwired/stimulus';

/**
 * "Change plan" dropdown for a single comparison table column.
 *
 * Toggles its own open/closed state and, on selection, dispatches
 * `change-plan:select` (bubbling up to the enclosing club-controller) with
 * the column slot index and chosen plan id so the memberships block can be
 * reloaded with that plan swapped into the slot.
 *
 * Option labels swap between short/annual titles when a matching
 * `plan-toggle:change` fires (same toggleId as membership/compare toggles).
 */
export default class extends Controller {
	static get targets () {
		return ['trigger', 'dropdown'];
	}

	static get values () {
		return {
			slot: Number,
		};
	}

	connect() {
		this.dropdownEl = this.dropdownTarget;

		this.onOutsideClick = this.onOutsideClick.bind(this);
		document.addEventListener('click', this.onOutsideClick);

		this.onReposition = this.onReposition.bind(this);
		window.addEventListener('resize', this.onReposition);
		window.addEventListener('scroll', this.onReposition, true);

		// Moved to document.body while open, so Stimulus's own data-action
		// binding (scoped to this.element's subtree) can't be relied on.
		this.select = this.select.bind(this);
		this.dropdownEl.addEventListener('change', this.select);

		this.onToggleChange = this.onToggleChange.bind(this);
		document.addEventListener('plan-toggle:change', this.onToggleChange);

		this.dropdownPlaceholder = document.createComment('change-plan-dropdown');
		this.dropdownEl.after(this.dropdownPlaceholder);

		this.syncTitlesFromToggles();
	}

	disconnect() {
		document.removeEventListener('click', this.onOutsideClick);
		window.removeEventListener('resize', this.onReposition);
		window.removeEventListener('scroll', this.onReposition, true);
		document.removeEventListener('plan-toggle:change', this.onToggleChange);
		this.dropdownEl.removeEventListener('change', this.select);
		this.dropdownEl.remove();
	}

	toggle() {
		const isOpen = !this.dropdownEl.hidden;
		this.setOpen(!isOpen);
	}

	setOpen(open) {
		this.dropdownEl.hidden = !open;
		this.element.classList.toggle('is-open', open);

		if (open) {
			document.body.appendChild(this.dropdownEl);
			this.positionDropdown();
			this.syncTitlesFromToggles();
		} else if (this.dropdownPlaceholder && this.dropdownPlaceholder.parentNode) {
			this.dropdownPlaceholder.parentNode.insertBefore(this.dropdownEl, this.dropdownPlaceholder);
		}
	}

	positionDropdown() {
		const rect = this.triggerTarget.getBoundingClientRect();
		const minWidth = Math.max(rect.width, 220);
		this.dropdownEl.style.position = 'fixed';
		this.dropdownEl.style.top = `${rect.bottom + 8}px`;
		this.dropdownEl.style.minWidth = `${minWidth}px`;

		const width = this.dropdownEl.offsetWidth || minWidth;
		const idealLeft = rect.left - (width - rect.width) / 2;
		const maxLeft = window.innerWidth - width - 8;
		this.dropdownEl.style.left = `${Math.min(Math.max(idealLeft, 8), Math.max(maxLeft, 8))}px`;
	}

	onReposition() {
		if (!this.dropdownEl.hidden) {
			this.positionDropdown();
		}
	}

	onOutsideClick(event) {
		if (!this.element.contains(event.target) && !this.dropdownEl.contains(event.target)) {
			this.setOpen(false);
		}
	}

	onToggleChange(event) {
		const { toggleId, checked } = event.detail;

		this.dropdownEl.querySelectorAll(`[data-toggle-id="${toggleId}"]`).forEach((item) => {
			this.applyTitle(item, checked);
		});
	}

	syncTitlesFromToggles() {
		this.dropdownEl.querySelectorAll('[data-toggle-id]').forEach((item) => {
			const toggle = document.querySelector(`[data-plan-toggle-id-value="${item.dataset.toggleId}"]`);
			const checked = toggle?.dataset.checked === 'true' || toggle?.classList.contains('is-checked');
			this.applyTitle(item, Boolean(checked));
		});
	}

	applyTitle(item, checked) {
		const titleEl = item.querySelector('[data-change-plan-title]');
		if (!titleEl) {
			return;
		}

		titleEl.textContent = checked
			? (item.dataset.titleAnnual || item.dataset.titleShort || titleEl.textContent)
			: (item.dataset.titleShort || titleEl.textContent);
	}

	select(event) {
		const planId = event.target.value;
		this.setOpen(false);

		this.element.dispatchEvent(new CustomEvent('change-plan:select', {
			bubbles: true,
			detail: {
				slot: this.slotValue,
				planId,
			},
		}));
	}
}
